<script async setup lang="ts">
import { CULTURE } from '@/composables/helpers'
import type { IAccoItem } from '@/composables/interfaces/apiInterfaces'
import { EnquiryType } from '@/composables/interfaces/appInterfaces'
import { useSaveEnquiryApi } from '@/composables/useApi'
import { RequestedCidsLocalStorage } from '@/constants'
import t from '@/i18n/translations'
import { enquiryData, trackEnquiry } from '@/store/enquiry.store'
import AccoItemPrice from './AccoItemPrice.vue'
import AccoItemRating from './AccoItemRating.vue'

const props = defineProps<{
  item: IAccoItem
}>()

async function sendDirectEnquiry(cid: number): Promise<void> {
  var button = document.getElementById(cid.toString()) as HTMLButtonElement
  const originalBtnText = button.innerText

  if (enquiryData.value != null) {
    const updatedEnquiryData = enquiryData.value
    updatedEnquiryData.enquiryType = EnquiryType.Direct
    updatedEnquiryData.companyIds = [cid]
    updatedEnquiryData.wishedOffers = 1
    updatedEnquiryData.filter = undefined
    updatedEnquiryData.tag = '' // no room information on extend enquiries
    updatedEnquiryData.track = JSON.stringify({ extend: 'enquiry-success' })
    button.disabled = true
    setExcludedCompanyIdToLocalStorage(cid)
    var result = await useSaveEnquiryApi(updatedEnquiryData)
    if (result.success) {
      trackEnquiry(updatedEnquiryData, result, false)
      button.innerText = t(`${CULTURE}.acco.requestSuccess`)
    } else {
      button.innerText = t(`${CULTURE}.acco.requestFailed`)
      setTimeout(() => {
        button.innerText = originalBtnText
        button.disabled = false
      }, 2000)
    }
  } else {
    button.innerText = t(`${CULTURE}.acco.requestFailed`)
    setTimeout(() => {
      button.innerText = originalBtnText
      button.disabled = false
    }, 2000)
  }
}

function setExcludedCompanyIdToLocalStorage(cid: number): void {
  var localStorageData = localStorage.getItem(RequestedCidsLocalStorage)
  if (localStorageData != null) {
    const storedData: number[] = JSON.parse(localStorageData)
    if (!storedData.includes(cid)) storedData.push(cid)
    localStorage.setItem(RequestedCidsLocalStorage, JSON.stringify(storedData))
  }
  localStorage.setItem(RequestedCidsLocalStorage, JSON.stringify([cid]))
}
</script>

<template>
  <div class="relative w-full hover:text-inherit hover:no-underline">
    <div class="pui-acco-card flex h-full min-h-32 flex-row overflow-hidden !rounded shadow-md md:border md:shadow-none">
      <div class="relative w-2/5">
        <div class="h-full overflow-hidden">
          <img class="aspect-video h-full w-full object-cover" :alt="props.item.companyName" :src="props.item.img" />
        </div>
      </div>
      <div class="relative flex min-h-40 w-3/5 flex-col justify-between gap-4 p-2 pl-3 leading-5 md:gap-10 md:p-4">
        <div>
          <AccoItemRating :is-superior="props.item.isSuperior" :rating="props.item.rating" :symbol-class="props.item.symbolClass" />
          <div class="mb-0 text-lg font-bold">{{ props.item.companyName }}</div>
          <div class="text-sm font-normal">{{ props.item.location }}</div>
        </div>
        <div class="ml-auto flex flex-col gap-2">
          <AccoItemPrice :min-price="props.item.minPrice" />
          <div class="actions relative text-right">
            <button
              :id="item.companyId.toString()"
              @click="$window.siteEvent?.('button', 'enquiry-success-extend', item.gaqLabel), sendDirectEnquiry(item.companyId)"
              class="myEnquiryButton z- w-full whitespace-nowrap rounded border border-primary px-2 py-1"
            >
              {{ t(`${CULTURE}.acco.requestNow`) }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
